import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Pets } from '@mui/icons-material';
import apiService from '../services/api';

/**
 * Step 1 of password reset: ask for the email, backend sends the link.
 *
 * The backend has had /api/users/password-reset/ all along, and emails a link
 * to {FRONTEND_URL}/reset-password — but neither this page nor that route
 * existed, so the whole flow dead-ended. There was no way into it from the
 * sign-in screen either.
 *
 * The response is deliberately identical whether or not the email has an
 * account (the backend always returns 200), so this screen must not reveal
 * the difference in its wording.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await apiService.requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(err.message || 'Could not send the reset link. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ py: { xs: 6, md: 10 } }}>
        <Paper sx={{ p: { xs: 3, md: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Pets sx={{ fontSize: 40, color: 'primary.main' }} aria-hidden="true" />
            <Typography variant="h5" component="h1" sx={{ mt: 1 }}>
              Reset your password
            </Typography>
          </Box>

          {sent ? (
            <>
              <Alert severity="success" sx={{ mb: 3 }}>
                If that email has an account, a reset link is on its way. The link
                is valid for 24 hours.
              </Alert>
              <Button component={RouterLink} to="/auth" fullWidth variant="outlined">
                Back to sign in
              </Button>
            </>
          ) : (
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Enter the email address on your account and we&apos;ll send you a
                link to set a new password.
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                label="Email address"
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                sx={{ boxShadow: 'none', '&:active': { boxShadow: 'none', transform: 'none' }, }}
                disabled={submitting || !email}
              >
                {submitting ? <CircularProgress size={24} /> : 'Send reset link'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link component={RouterLink} to="/auth" variant="body2">
                  Back to sign in
                </Link>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
}
